import { makeStyles, tokens } from '@fluentui/react-components';
import { ottoTokens } from '@/theme/ottoTokens';

/**
 * Visual styles for a single monitoring list row (used by both run history
 * and trigger history). Status indicator + left-border accent + selected /
 * focused states mirror the original WorkflowRunsPanel row exactly so both
 * surfaces look identical.
 */
export const useMonitoringListItemStyles = makeStyles({
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    padding: tokens.spacingVerticalM,
    paddingInline: tokens.spacingHorizontalM,
    cursor: 'pointer',
    border: 'none',
    width: '100%',
    textAlign: 'left',
    backgroundColor: 'transparent',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    borderLeft: `${tokens.strokeWidthThicker} solid transparent`,
    transitionProperty: 'background-color, border-left-color, transform',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      transform: `translateX(${tokens.spacingHorizontalXXS})`,
    },
    ':focus-visible': {
      outline: `${tokens.strokeWidthThick} solid ${tokens.colorBrandStroke1}`,
      outlineOffset: `calc(-1 * ${tokens.strokeWidthThick})`,
    },
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
      ':hover': {
        transform: 'none',
      },
    },
  },
  rowFailed: {
    borderLeftColor: ottoTokens.statusFailed,
  },
  rowSelected: {
    backgroundColor: tokens.colorBrandBackground2,
    borderLeftColor: tokens.colorBrandStroke1,
    transform: 'none',
    ':hover': {
      backgroundColor: tokens.colorBrandBackground2,
      transform: 'none',
    },
  },
  rowFocused: {
    outline: `${tokens.strokeWidthThick} solid ${tokens.colorBrandStroke1}`,
    outlineOffset: `calc(-1 * ${tokens.strokeWidthThick})`,
  },

  statusIndicator: {
    width: tokens.spacingHorizontalMNudge,
    height: tokens.spacingHorizontalMNudge,
    borderRadius: '50%',
    flexShrink: 0,
    boxShadow: `0 0 0 ${tokens.strokeWidthThick} ${tokens.colorNeutralBackground1}`,
  },
  statusSucceeded: { backgroundColor: ottoTokens.statusSucceeded },
  statusFailed: { backgroundColor: ottoTokens.statusFailed },
  statusRunning: {
    backgroundColor: ottoTokens.statusRunning,
    boxShadow: `0 0 0 ${tokens.strokeWidthThick} ${tokens.colorNeutralBackground1}, 0 0 ${tokens.spacingHorizontalS} ${ottoTokens.statusRunning}`,
  },
  statusCancelled: { backgroundColor: ottoTokens.statusCancelled },
  statusSkipped: { backgroundColor: ottoTokens.statusSkipped },

  info: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  primaryRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  primaryText: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  metaText: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  trailingText: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    fontWeight: tokens.fontWeightMedium,
    textAlign: 'right',
    whiteSpace: 'nowrap',
    fontFamily: 'monospace',
  },
});
